<script setup lang="ts">
import type { KanbanComment } from '@entities/kanban-comment'

import { AppText } from '@shared/ui/typography'

import { useKanbanCommentItem } from '../../model/comment/comment-item.hook'
import KanbanCommentTaskMeta from './KanbanCommentTaskMeta.vue'

defineOptions({
  name: 'KanbanCommentItem',
})

const props = withDefaults(defineProps<KanbanCommentItemProps>(), {
  additionalData: false,
})

const emit = defineEmits<{
  'click:edit': [KanbanComment]
}>()

interface KanbanCommentItemProps {
  comment: KanbanComment
  additionalData?: boolean
}

const {
  refAgentName,
  refAgentUrl,
  refDate,
  refSegments,
  refStatusLabel,
  refStatusColor,
  handleClickEdit,
} = useKanbanCommentItem({
  props,
  onEdit: () => emit('click:edit', props.comment),
})
</script>

<template>
  <div class="kanban-comment-item">
    <a
      v-if="refAgentUrl"
      class="kanban-comment-item__agent"
      :href="refAgentUrl"
    >
      {{ refAgentName }}
    </a>
    <AppText
      v-else
      name="body2-medium"
      color="slate-500"
    >
      {{ refAgentName }}
    </AppText>
    <AppText
      name="body1"
      class="kanban-comment-item__body"
    >
      <template
        v-for="(segment, index) in refSegments"
        :key="index"
      >
        <a
          v-if="segment.type === 'link'"
          class="kanban-comment-item__link"
          :href="segment.href"
          target="_blank"
          rel="noopener noreferrer"
        >{{ segment.value }}</a>
        <template v-else>
          {{ segment.value }}
        </template>
      </template>
    </AppText>
    <div class="kanban-comment-item__controls">
      <KanbanCommentTaskMeta
        v-if="comment.kanban_task && additionalData"
        :task="comment.kanban_task"
      />
      <AppText
        name="body2"
        color="slate-500"
        class="kanban-comment-item__date"
      >
        {{ refDate }}
      </AppText>
      <AppText
        name="body2"
        color="blue-600"
        class="kanban-comment-item__edit"
        @click="handleClickEdit"
      >
        Edit
      </AppText>
      <AppText
        v-if="comment.kanban_task && additionalData"
        name="body1-medium"
        :color="refStatusColor"
        class="kanban-comment-item__status"
      >
        {{ refStatusLabel }}
      </AppText>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./KanbanCommentItem.scss" />
